import React from "react"
import { Box, Typography } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { useInterval } from "@coast/components"
import { formatMoney } from "../../utils/strings"
import { withDynamicApp } from "../../containers/withDynamicApp"
import { useLessonCouponMatch } from "../../hooks/useLessonCouponMatch"
import { GQLCouponType } from "../../graphqlTypes"

function getTimeLeft(expiration: number) {
  let diff = expiration - Date.now()

  if (diff <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
    }
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  diff %= 1000 * 60 * 60 * 24
  const hours = Math.floor(diff / (1000 * 60 * 60))
  diff %= 1000 * 60 * 60
  const minutes = Math.floor(diff / (1000 * 60))
  diff %= 1000 * 60
  const seconds = Math.floor(diff / 1000)

  return {
    days,
    hours,
    minutes,
    seconds,
  }
}

interface LessonCouponBannerProps {
  address: string | null
}

function InnerLessonCouponBanner({ address }: LessonCouponBannerProps) {
  const [, rerender] = React.useState(0)
  const theme = useTheme()
  useInterval(() => rerender((prev) => prev + 1), 1000)
  const { coupon, regionId } = useLessonCouponMatch(address)

  if (!coupon) {
    return null
  }

  const timeLeft = coupon.expiration ? getTimeLeft(coupon.expiration) : null

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: theme.palette.primary.main,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: "21px",
        color: "#ffffff",
      }}
    >
      <Typography variant="h4" component="p" fontWeight="bold">
        Great news!
      </Typography>
      <Typography
        component="p"
        textAlign="center"
        lineHeight="32px"
        margin="12px"
      >
        {coupon.type === GQLCouponType.Lesson ? "Lesson" : "Road Test"} packages{" "}
        {regionId ? (
          <>
            in{" "}
            <Typography component="span" fontWeight="bold">
              {regionId}{" "}
            </Typography>
          </>
        ) : null}
        are currently an additional{" "}
        <Typography component="span" fontWeight="bold">
          {formatMoney(coupon.discount)}
        </Typography>{" "}
        off for first-time students.
      </Typography>
      <Typography component="p">
        Use code{" "}
        <Typography
          component="span"
          fontWeight="bold"
          color="white"
          borderRadius="8px"
          padding="4px 8px"
          style={{ backgroundColor: "#F578BA" }}
        >
          {coupon.code}
        </Typography>{" "}
        at checkout.
      </Typography>

      {timeLeft ? (
        <Box
          sx={{
            flexDirection: "column",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mt: 2,
          }}
        >
          <Typography variant="h5" component="p" fontWeight="bold">
            Offer ends in
          </Typography>
          <Typography
            component="p"
            fontWeight="bold"
            fontSize="40px"
            width="265px"
            textAlign="center"
            fontFamily="monospace"
          >
            {timeLeft.days.toString().padStart(2, "0")}:
            {timeLeft.hours.toString().padStart(2, "0")}:
            {timeLeft.minutes.toString().padStart(2, "0")}:
            {timeLeft.seconds.toString().padStart(2, "0")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "space-between",
              pl: 1,
              pr: 1,
            }}
          >
            <span>days</span>
            <span>hours</span>
            <span>mins</span>
            <span>secs</span>
          </Box>
        </Box>
      ) : null}
    </Box>
  )
}

export const LessonCouponBanner = withDynamicApp(InnerLessonCouponBanner)
